/**
 * Hook that returns a TokenStore bound to the current AuthContext.
 * Pass the returned store to apiFetch() or any route-group API function.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, type AuthContextValue } from '../lib/auth-context.tsx';
import type { TokenStore, RefreshResponse } from './client.ts';

export function useTokenStore(): TokenStore {
  const auth: AuthContextValue = useAuth();
  const navigate = useNavigate();

  const getToken = useCallback(() => auth.accessToken, [auth.accessToken]);

  const onRefresh = useCallback(
    (data: RefreshResponse) => {
      auth.setTokens({
        accessToken: data.accessToken,
        userId: data.id,
        role: data.role,
        practiceId: auth.practiceId, // preserve active practice across refresh
      });
    },
    [auth],
  );

  const onAuthFailure = useCallback(() => auth.clearTokens(), [auth]);

  // Where the refused call was made, so the MFA screens return there (an invitation link).
  const onMfaRequired = useCallback((upgrade: boolean) => {
    const here = window.location.pathname + window.location.search;
    void navigate(upgrade ? '/mfa/setup' : '/mfa', { replace: true, ...(here.startsWith('/mfa') ? {} : { state: { from: here } }) });
  }, [navigate]);

  return { getToken, onRefresh, onAuthFailure, onMfaRequired };
}
